<template>
  <div class="verifications-list">
    <div class="verifications-list__filters app-list-filters">
      <input-field
        class="app-list-filters__field verifications-list__field"
        v-model.trim="filters.id"
        :label="'verifications-list.lbl-id' | globalize"
      />

      <input-field
        class="app-list-filters__field verifications-list__field"
        v-model.trim="filters.userId"
        :label="'verifications-list.lbl-user-id' | globalize"
      />

      <input-field
        class="app-list-filters__field verifications-list__field"
        v-model.trim="filters.email"
        :label="'verifications-list.lbl-email' | globalize"
      />

      <select-field
        class="app-list-filters__field verifications-list__field"
        v-model.trim="filters.status"
        :label="'verifications-list.lbl-verify-status' | globalize"
      >
        <option
          v-for="(item, id) in verificationStatuses"
          :key="`${item}-${id}`"
          :value="item"
        >
          {{ item | formatVerificationStatus }}
        </option>
      </select-field>

      <select-field
        class="app-list-filters__field verifications-list__field"
        v-model.trim="filters.type"
        :label="'verifications-list.lbl-type' | globalize"
      >
        <option
          v-for="(item, id) in verificationTypes"
          :key="`${item}-${id}`"
          :value="item"
        >
          {{ item | formatVerificationType }}
        </option>
      </select-field>

      <select-field
        class="app-list-filters__field verifications-list__field"
        v-model.trim="filters.subtype"
        :label="'verifications-list.lbl-subtype' | globalize"
      >
        <option
          v-for="(item, id) in verificationSubtypes"
          :key="`${item}-${id}`"
          :value="item"
        >
          {{ item | formatVerificationSubtype }}
        </option>
      </select-field>
      <date-field
        class="app-list-filters__field"
        v-model="filters.createdFrom"
        :label="'logs-list.lbl-start-date' | globalize"
        :enable-time="false"
      />
      <date-field
        class="app-list-filters__field"
        v-model="filters.createdTo"
        :label="'logs-list.lbl-end-date' | globalize"
        :enable-time="false"
      />
    </div>

    <div class="verifications-list__wrp app-list">
      <template v-if="isLoaded && verificationsList.length && !isFailed">
        <a-table
          class="ant-table"
          size="small"
          :data-source="verificationsList"
          :columns="columns"
          :row-key="(record) => record.id"
          :pagination="false"
          :loading="isPending"
          :row-class-name="rowClassName"
        >
          <template
            slot="id-column"
            slot-scope="id">
            <copyable :copy-text="id">
              <a-button
                type="link"
                class="cursor-pointer"
                @click="redirectToVerificationDetails(id)">
                {{ id }}
              </a-button>
            </copyable>
          </template>
          <template
            slot="userId-column"
            slot-scope="userId">
            <copyable :copy-text="userId.toString()">
              <a-button
                type="link"
                @click="redirectToUserDetails(userId)">
                {{ userId }}
              </a-button>
            </copyable>
          </template>
          <template
            slot="email-column"
            slot-scope="email">
            <copyable :copy-text="email">
              {{ email }}
            </copyable>
          </template>
          <template
            slot="status-column"
            slot-scope="status">
            {{ status | formatVerificationStatus }}
          </template>
          <template
            slot="type-column"
            slot-scope="type">
            {{ type | formatVerificationType }}
          </template>
          <template
            slot="subtype-column"
            slot-scope="subtype">
            {{ subtype | formatVerificationSubtype }}
          </template>

          <template
            slot="createdAt-column"
            slot-scope="createdAt">
            <span
              class="app-list__cell app-list__cell--left"
              :title="createdAt"
            >
              {{ createdAt | formatDateDMYT }}
            </span>
          </template>
          <template
            slot="actions-column"
            slot-scope="id">
            <a-dropdown>
              <a
                class="ant-dropdown-link"
                @click="e => e.preventDefault()">
                Actions <a-icon type="down" />
              </a>
              <a-menu slot="overlay">
                <a-menu-item :key="`export-${id}`">
                  <export-pdf-button
                    document-id="verification-as-pdf"
                    :route-path="`/verification/${id}/export`"
                    :export-from-list="true"
                    :before-click="() => beforeExportClick(id)"
                    button-type="default"
                  />
                </a-menu-item>
                <a-menu-item :key="`details-${id}`">
                  <a-button
                    type="default"
                    title="Details"
                    @click="redirectToVerificationDetails(id)"
                  >
                    {{ 'verifications-list.lbl-to-details' | globalize }}
                    <i class="mdi mdi-account-card-details" />
                  </a-button>
                </a-menu-item>
              </a-menu>
            </a-dropdown>
          </template>
        </a-table>
      </template>

      <template v-else>
        <div class="app-list__li-like">
          <template v-if="isLoaded && !verificationsList.length">
            <p>
              {{ "verifications-list.nothing-here-yet" | globalize }}
            </p>
          </template>

          <template v-else-if="isFailed">
            <p>
              {{ "verifications-list.default-error-message" | globalize }}
            </p>
          </template>

          <template v-else>
            <p>
              {{ "verifications-list.loading-message" | globalize }}
            </p>
          </template>
        </div>
      </template>
    </div>

    <collection-loader
      class="app__more-btn-wrp"
      :first-page-loader="getVerifications"
      @first-page-load="setList"
      @next-page-load="concatList"
      ref="collectionLoaderBtn"
    />
  </div>
</template>

<script>
import CollectionLoader from '@/vue/common/CollectionLoader'
import SelectField from '@/vue/fields/SelectField'
import DateField from '@/vue/fields/DateField'
import InputField from '@/vue/fields/InputField'
import Copyable from '@/vue/common/copyable/Copyable'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { clearObject } from '@/js/helpers/clear-object'
import { api } from '@/api'
import { globalize } from '@/vue/filters/globalize'
import { vueRoutes } from '@/vue-router/routes'
import { VerificationRecord, VERIFICATION_SUBTYPES } from '@/js/records/entities/verification.record'
import { addParamsToLocation, queryToFilters } from '@/js/helpers/router-query'
import PermissionMixin from '@/vue/mixins/permission.mixin'
import ExportPdfButton from '@/vue/common/export-pdf-button'
import ATable from 'ant-design-vue/lib/table'
import AButton from 'ant-design-vue/lib/button'
import ADropdown from 'ant-design-vue/lib/dropdown'
import AMenu from 'ant-design-vue/lib/menu'
import AIcon from 'ant-design-vue/lib/icon'

const defaultFilters = {
  id: '',
  userId: '',
  email: '',
  type: '',
  subtype: '',
  status: '',
  createdFrom: null,
  createdTo: null,
}

export default {
  name: 'verifications-list',
  components: {
    CollectionLoader,
    SelectField,
    InputField,
    DateField,
    'copyable': Copyable,
    ExportPdfButton,
    ATable,
    AButton,
    'a-dropdown': ADropdown,
    'a-menu': AMenu,
    'a-menu-item': AMenu.Item,
    AIcon,
  },
  mixins: [ PermissionMixin ],

  data () {
    return {
      isLoaded: false,
      isFailed: false,
      isPending: false,
      verificationsList: [],
      // eduard20028 Hardcode filters, due to the lack of an endpoint
      verificationStatuses: ['in_progress', 'pending_manual', 'approved', 'rejected', 'failed'],
      verificationTypes: ['personal_data', 'email', 'payment', 'doc_sign', 'manual', 'kyb'],
      verificationSubtypes: VERIFICATION_SUBTYPES,
      filters: defaultFilters,
    }
  },
  computed: {
    verificationDetailsDisabled () {
      const result = this.isForbidden('verifications', 'verification', 'read_foreign')
      return result
    },
    columns () {
      return [
        {
          title: globalize('verifications-list.lbl-id'),
          dataIndex: 'id',
          key: 'id',
          scopedSlots: { customRender: 'id-column' },
        },
        {
          title: globalize('verifications-list.lbl-user-id'),
          dataIndex: 'userId',
          key: 'userId',
          scopedSlots: { customRender: 'userId-column' },
        },
        {
          title: globalize('verifications-list.lbl-email'),
          dataIndex: 'email',
          key: 'email',
          scopedSlots: { customRender: 'email-column' },
        },
        {
          title: globalize('verifications-list.lbl-verify-status'),
          dataIndex: 'status',
          key: 'status',
          scopedSlots: { customRender: 'status-column' },
        },
        {
          title: globalize('verifications-list.lbl-type'),
          dataIndex: 'type',
          key: 'type',
          scopedSlots: { customRender: 'type-column' },
        },
        {
          title: globalize('verifications-list.lbl-subtype'),
          dataIndex: 'subtype',
          key: 'subtype',
          scopedSlots: { customRender: 'subtype-column' },
        },
        {
          title: globalize('verifications-list.lbl-created'),
          dataIndex: 'createdAt',
          key: 'createdAt',
          scopedSlots: { customRender: 'createdAt-column' },
        },
        {
          title: globalize('verifications-list.lbl-actions'),
          dataIndex: 'id',
          key: 'actions',
          scopedSlots: { customRender: 'actions-column' },
        },
      ]
    },
  },
  watch: {
    filters: {
      handler () {
        this.isPending = true
        if (this.isLoaded) {
          this.reloadList()
        }
      },
      deep: true,
    },
    isLoaded: {
      handler (value) {
        if (value && this.isPending) {
          this.reloadList()
          this.isPending = false
        }
      },
    },
    '$route' () {
      const filters = queryToFilters(defaultFilters)
      this.filters = filters
      return this.getVerifications()
    },
  },

  async created () {
    this.filters = queryToFilters(defaultFilters)
    await this.getVerifications()
  },
  methods: {
    async getUserIds () {
      let userIds = []
      try {
        const query = {
          filter: clearObject({
            email: this.filters.email,
          }),
        }

        let { data } = await api.getWithSignature('/users', query)
        data.forEach((element) => {
          userIds.push(element.id)
        })
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
      return userIds
    },

    async getUser (userId) {
      let response = {}
      try {
        const query = {
          filter: clearObject({
            user_id: userId,
          }),
        }

        response = await api.getWithSignature('/users', query)
        return response
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async getVerifications () {
      this.isLoaded = false
      this.isFailed = false
      let response = {}
      let query = {}

      try {
        let usersIds = await this.getUserIds()
        if (this.filters.email) {
          query = {
            filter: clearObject({
              user_id: usersIds.join(),
            }),
          }
        } else {
          query = {
            filter: clearObject({
              id: this.filters.id,
              user_id: this.filters.userId,
              type: this.filters.type,
              subtype: this.filters.subtype,
              status: this.filters.status,
              created_from: this.filters.createdFrom
                ? new Date(this.filters.createdFrom).toISOString()
                : null,
              created_to: this.filters.createdTo
                ? new Date(this.filters.createdTo).toISOString()
                : null,
            }),
          }
        }

        response = await api.getWithSignature('/verifications', query)

        return response
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.isLoaded = true
        this.isPending = false
      }
    },

    setList (data) {
      this.verificationsList = data.map((i) => new VerificationRecord(i))
    },

    concatList (data) {
      this.verificationsList = this.verificationsList.concat(
        data.map((i) => new VerificationRecord(i))
      )
    },

    reloadList () {
      addParamsToLocation(this.$route, this.filters)
      this.$refs.collectionLoaderBtn.loadFirstPage()
    },

    redirectToVerificationDetails (id) {
      this.$router.push({
        name: vueRoutes.verificationDetails.name,
        params: { id },
      })
    },

    beforeExportClick (id) {
      this.$router.push({
        name: vueRoutes.verificationDetailsExport.name,
        params: { id },
      })
    },

    redirectToUserDetails (id) {
      this.$router.push({
        name: vueRoutes.userDetails.name,
        params: { id },
      })
    },

    rowClassName (record, index) {
      return (index % 2 === 1 ? 'ant-table-striped' : null)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~ant-design-vue/lib/table/style/index.css';
@import '~ant-design-vue/lib/button/style/index.css';
@import '~ant-design-vue/lib/dropdown/style/index.css';
@import '~ant-design-vue/lib/icon/style/index.css';

.verifications-list__wrp {

  padding-top: 4rem;

  .ant-dropdown-link {
    cursor: pointer;
  }
}

.ant-dropdown-menu-item {
  .ant-btn {
    width: 100%;

    i {
      padding-left: 1em;
    }
  }
}

.verifications-list__field {
  min-width: 16.5rem;
}

</style>
